import type {
  ApiError,
  ArtworkView,
  CreateGenerationInput,
  CreateOrderInput,
  GenerationView,
  OrderView,
  PaymentStatusView,
  ProductQuotesView,
  PublicUser,
  QuotaView,
  UpdateArtworkInput,
} from "../shared/contracts";
import type { ProductDefinition } from "../shared/products";

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fields?: Record<string, string[]>,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

export interface MeView {
  user: PublicUser;
  quota: QuotaView;
  stats: { artworks: number; favorites: number };
}

export interface CapabilitiesView {
  payments: {
    fubei: boolean;
    mode: "wap";
    testMode: boolean;
  };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const apiError = payload as ApiError | null;
    throw new ApiClientError(
      response.status,
      apiError?.error.code ?? "REQUEST_FAILED",
      apiError?.error.message ?? "请求失败，请稍后重试",
      apiError?.error.fields,
    );
  }
  return (payload as { data: T }).data;
}

export const api = {
  products: () => request<ProductDefinition[]>("/api/products"),
  productQuotes: () => request<ProductQuotesView>("/api/products/quotes"),
  capabilities: () => request<CapabilitiesView>("/api/capabilities"),
  me: () => request<MeView>("/api/users/me"),
  logout: () => request<{ loggedOut: boolean }>("/api/auth/logout", { method: "POST" }),
  deleteAccount: () =>
    request<{ deleted: boolean }>("/api/account", {
      method: "DELETE",
      body: JSON.stringify({ confirmation: "DELETE" }),
    }),
  createGeneration: (input: CreateGenerationInput) =>
    request<GenerationView>("/api/generations", { method: "POST", body: JSON.stringify(input) }),
  generation: (id: string) => request<GenerationView>("/api/generations/" + encodeURIComponent(id)),
  artwork: (id: string) => request<ArtworkView>("/api/artworks/" + encodeURIComponent(id)),
  artworks: (filter: "all" | "desktop" | "mobile" | "favorite") =>
    request<{ items: ArtworkView[]; nextCursor: string | null }>("/api/artworks?filter=" + filter),
  updateArtwork: (id: string, input: UpdateArtworkInput) =>
    request<ArtworkView>("/api/artworks/" + encodeURIComponent(id), {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  deleteArtwork: (id: string) =>
    request<{ undoUntil: string }>("/api/artworks/" + encodeURIComponent(id), { method: "DELETE" }),
  restoreArtwork: (id: string) =>
    request<ArtworkView>("/api/artworks/" + encodeURIComponent(id) + "/restore", { method: "POST" }),
  downloadArtwork: (id: string) =>
    request<{ url: string; variant: "original" | "1080"; expiresAt: string }>(
      "/api/artworks/" + encodeURIComponent(id) + "/download-url",
      { method: "POST" },
    ),
  createOrder: (input: CreateOrderInput) =>
    request<{
      order: OrderView;
      paymentPageUrl: string;
      expiresAt: string;
    }>("/api/orders", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  orderByTradeNo: (outTradeNo: string) =>
    request<OrderView>("/api/orders/by-trade-no/" + encodeURIComponent(outTradeNo)),
  fubeiPaymentStatus: (input: {
    orderId: string;
    outTradeNo: string;
    expires: string;
    signature: string;
  }) =>
    request<PaymentStatusView>(
      "/api/payments/fubei/status?" + new URLSearchParams(input).toString(),
    ),
  track: (name: string, properties: Record<string, unknown> = {}) =>
    request<{ accepted: boolean }>("/api/events", {
      method: "POST",
      body: JSON.stringify({ name, properties }),
    }).catch(() => undefined),
};
